import React, { useEffect, useState } from 'react';
import BOLayout from '@/pages/back-office/BOLayout';
import axios from 'axios';
import { toast } from 'react-toastify';
import Pen from '@/assets/media/SVGComponents/Pen';
import Trash from '@/assets/media/SVGComponents/Trash';
import { useNavigate } from 'react-router-dom';
import Modal from '@/components/Modal';
import SettingsTopBanner from '@/components/back-office/SettingsTopBanner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import Drag from '@/assets/media/SVGComponents/Drag';
import BONavigationButton from '@/components/back-office/BONavigationButton';
import { Switch } from '@headlessui/react';

const CategoriesList = () => {
    const [categories, setCategories] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [categoryId, setCategoryId] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const navigate = useNavigate();
    const role = JSON.parse(localStorage.getItem('role'));

    useEffect(() => {
        if (role && role !== 'super-admin') {
            toast.info('Zone interdite', {
                position: 'top-center',
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                theme: 'colored',
            });

            navigate('/back-office');

            return;
        }

        const fetchData = async () => {
            try {
                const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/settings/categories/list?show_hidden=true`);

                if (response.status === 200) {
                    setCategories(response.data);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
                toast.error('Une erreur est survenue', {
                    position: 'top-center',
                    autoClose: 3500,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: false,
                    draggable: true,
                    theme: 'colored',
                });
            }
        };

        fetchData();
    }, []);

    const handleOpenModal = (e, category) => {
        setShowModal(true);
        setCategoryId(category.id);
        setSelectedCategory(category);
    };

    const handleModalSubmit = async () => {
        try {
            await axios.delete(`${import.meta.env.VITE_API_URL}/api/back-office/settings/categories/${categoryId}/delete`);

            toast.success('Categorie supprimée avec succès', {
                position: 'top-center',
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                theme: 'colored',
            });
        } catch (error) {
            console.error('Error deleting category:', error);
            toast.error('Une erreur est survenue', {
                position: 'top-center',
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                theme: 'colored',
            });
        }

        setTimeout(() => {
            navigate(0);
        }, 1500);
    };

    const handleDropSubmit = async (updatedCategories) => {
        const formData = new FormData();
        formData.append('categories', JSON.stringify(updatedCategories));

        try {
            await axios.put(
                `${import.meta.env.VITE_API_URL}/api/back-office/settings/categories/update-priorities`,
                formData
            );
        } catch (error) {
            console.error('Error editing category:', error);
        }

        navigate(0);
    };

    const toggleCategoryVisibility = async (category) => {
        try {
            const formData = new FormData();
            formData.append('name', category.name);
            formData.append('is_hidden', (!category.is_hidden).toString());

            await axios.put(
                `${import.meta.env.VITE_API_URL}/api/back-office/settings/categories/${category.id}/update`,
                formData
            );

            // Mettre à jour l'état local
            setCategories(prevCategories => 
                prevCategories.map(cat => 
                    cat.id === category.id ? { ...cat, is_hidden: !cat.is_hidden } : cat
                )
            );

            toast.success(`Catégorie ${!category.is_hidden ? 'masquée' : 'visible'} avec succès`, {
                position: 'top-center',
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                theme: 'colored',
            });
        } catch (error) {
            console.error('Error updating category visibility:', error);
            toast.error('Une erreur est survenue', {
                position: 'top-center',
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                theme: 'colored',
            });
        }
    };

    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const reorderedCategories = Array.from(categories);
        const [movedCategory] = reorderedCategories.splice(result.source.index, 1);
        reorderedCategories.splice(result.destination.index, 0, movedCategory);

        // Assign new priorities based on the new order
        const updatedCategories = reorderedCategories.map((category, index) => ({
            ...category,
            priority: index + 1,
        }));

        setCategories(updatedCategories);

        // Log the updated categories with their new priorities
        handleDropSubmit(updatedCategories);
    };

    return (
        <BOLayout>
            <div className="mb-4 grid grid-cols-1 gap-12">
                <SettingsTopBanner />
                <h1 className="text-3xl font-bold text-center">Liste des catégories</h1>
                <BONavigationButton
                    text={'Créer une catégorie '}
                    navigationRoute={`/back-office/settings/categories/creation`}
                />
            </div>

            {showModal && (
                <Modal
                    type={'danger'}
                    title={'Cette action est irréversible'}
                    setShowModal={setShowModal}
                    handleSubmit={handleModalSubmit}
                    image={selectedCategory?.picture_url}
                >
                    <p className="text-center font-semibold mt-4">
                        Êtes-vous sûr de vouloir supprimer la catégorie "{selectedCategory?.name}" ?
                    </p>
                </Modal>
            )}

            <table className="table table-striped table-bordered table-fixed w-full text-center">
                <thead className="border-b border-b-black pb-2 mb-4">
                    <tr>
                        <th className="w-10"></th>
                        <th>Nom</th>
                        <th>Image</th>
                        <th>Priorité</th>
                        <th>Visible</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="steps">
                        {(provided) => (
                            <tbody {...provided.droppableProps} ref={provided.innerRef}>
                                {categories.map((category, index) => (
                                    <Draggable key={category.id} draggableId={`${category.id}`} index={index}>
                                        {(provided) => (
                                            <tr
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className="mb-2"
                                            >
                                                <td>
                                                    <Drag height={10} width={10} />{' '}
                                                </td>
                                                <td>{category.name}</td>
                                                <td>
                                                    {category.picture_url && (
                                                        <img 
                                                            src={category.picture_url} 
                                                            alt={category.name} 
                                                            className="w-[50px] h-[50px] object-cover rounded-lg mx-auto"
                                                        />
                                                    )}
                                                </td>
                                                <td>{category.priority}</td>
                                                <td>
                                                    <Switch
                                                        checked={!category.is_hidden}
                                                        onChange={() => toggleCategoryVisibility(category)}
                                                        className={`${
                                                            !category.is_hidden ? 'bg-green-600' : 'bg-red-600'
                                                        } relative inline-flex h-6 w-11 items-center rounded-full`}
                                                    >
                                                        <span className="sr-only">Visibilité de la catégorie</span>
                                                        <span
                                                            className={`${
                                                                !category.is_hidden ? 'translate-x-6' : 'translate-x-1'
                                                            } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                                                        />
                                                    </Switch>
                                                </td>
                                                <td className="flex items-center justify-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate(`/back-office/settings/categories/${category.id}/edition`)}
                                                    >
                                                        <Pen color="#A2924A" width={40} height={40} />
                                                    </button>

                                                    <button type="button" className="m-2" onClick={(e) => handleOpenModal(e, category)}>
                                                        <Trash color="#A2924A" width={40} height={40} />
                                                    </button>
                                                </td>
                                            </tr>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </tbody>
                        )}
                    </Droppable>
                </DragDropContext>
            </table>
        </BOLayout>
    );
};

export default CategoriesList;
